import { readonly, ref, type Ref } from "vue";

export interface UsePollingOptions<TResult> {
  operation: (signal: AbortSignal) => Promise<TResult>;
  onResult: (result: TResult) => void | Promise<void>;
  onFailure: (error: unknown, consecutiveFailures: number) => void | Promise<void>;
  nextDelay: (result: TResult) => number | null;
  retryDelay: (error: unknown, consecutiveFailures: number) => number | null;
}

export interface PollingController {
  readonly isRunning: Readonly<Ref<boolean>>;
  readonly consecutiveFailures: Readonly<Ref<number>>;
  start(delayMs?: number): void;
  runNow(): Promise<void>;
  schedule(delayMs: number): void;
  stop(): void;
  dispose(): void;
}

function assertDelay(delayMs: number): void {
  if (!Number.isFinite(delayMs) || delayMs < 0) {
    throw new RangeError("Polling delay must be a finite non-negative number.");
  }
}

export function usePolling<TResult>(options: UsePollingOptions<TResult>): PollingController {
  const isRunning = ref(false);
  const consecutiveFailures = ref(0);
  let active = false;
  let generation = 0;
  let requestController: AbortController | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;

  function clearTimer(): void {
    if (timer !== undefined) {
      clearTimeout(timer);
      timer = undefined;
    }
  }

  function abortRequest(): void {
    requestController?.abort();
    requestController = undefined;
  }

  function accepts(generationAtStart: number, controller: AbortController): boolean {
    return (
      active &&
      generationAtStart === generation &&
      requestController === controller &&
      !controller.signal.aborted
    );
  }

  function finish(): void {
    clearTimer();
    active = false;
    isRunning.value = false;
  }

  function queue(delayMs: number): void {
    timer = setTimeout(() => {
      timer = undefined;
      if (active) {
        void runNow().catch(() => undefined);
      }
    }, delayMs);
  }

  function schedule(delayMs: number): void {
    assertDelay(delayMs);
    if (!active) {
      return;
    }
    clearTimer();
    queue(delayMs);
  }

  function stop(): void {
    generation += 1;
    clearTimer();
    abortRequest();
    active = false;
    isRunning.value = false;
  }

  function start(delayMs?: number): void {
    if (delayMs !== undefined) {
      assertDelay(delayMs);
    }

    generation += 1;
    clearTimer();
    abortRequest();
    consecutiveFailures.value = 0;
    active = true;
    isRunning.value = true;

    if (delayMs === undefined) {
      void runNow().catch(() => undefined);
      return;
    }
    queue(delayMs);
  }

  async function runNow(): Promise<void> {
    const wasActive = active;
    if (!wasActive) {
      active = true;
      isRunning.value = true;
      consecutiveFailures.value = 0;
    }

    clearTimer();
    abortRequest();
    generation += 1;
    const generationAtStart = generation;
    const controller = new AbortController();
    requestController = controller;

    let result: TResult;
    try {
      result = await options.operation(controller.signal);
    } catch (error) {
      if (!accepts(generationAtStart, controller)) {
        return;
      }

      const failures = consecutiveFailures.value + 1;
      consecutiveFailures.value = failures;
      try {
        await options.onFailure(error, failures);
      } catch {
        // Failure reporting must not prevent the retry lifecycle from completing.
      }
      if (!accepts(generationAtStart, controller)) {
        return;
      }

      const delay = options.retryDelay(error, failures);
      if (delay === null) {
        finish();
      } else {
        schedule(delay);
      }
      throw error;
    }

    if (!accepts(generationAtStart, controller)) {
      return;
    }

    consecutiveFailures.value = 0;
    let resultCallbackFailed = false;
    let resultCallbackError: unknown;
    try {
      await options.onResult(result);
    } catch (error) {
      resultCallbackFailed = true;
      resultCallbackError = error;
    }
    if (!accepts(generationAtStart, controller)) {
      return;
    }

    const delay = options.nextDelay(result);
    if (delay === null) {
      finish();
    } else {
      schedule(delay);
    }
    if (resultCallbackFailed) {
      throw resultCallbackError;
    }
  }

  return {
    isRunning: readonly(isRunning),
    consecutiveFailures: readonly(consecutiveFailures),
    start,
    runNow,
    schedule,
    stop,
    dispose: stop,
  };
}
